import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { COMPANY_BRAND_NAME } from '../../support/brand';
import { PANEL_LOGIN_URL, PANEL_LEAD_URL } from '../../support/publicUrls';
import {
  AUDIENCE,
  CLOSING,
  CONTACT_URL,
  FAQS,
  FAQ_SECTION,
  FEATURES,
  FEATURES_SECTION,
  HERO,
  INTRODUCTION,
  LAST_MODIFIED_LABEL,
  RECENT_ARTICLES,
  RECENT_ARTICLES_SECTION,
  SEO,
  SHOWCASES,
  SHOWCASE_SECTION,
  CLIENTS,
  CLIENTS_GROUP,
  PARTNERS,
  PARTNERS_GROUP,
  TRUST_SECTION,
  STEPS,
  STEPS_SECTION,
  RELATED_LINKS,
} from './content';
import { META_TAGS, STRUCTURED_DATA } from './seo';

const FEATURE_ICONS = {
  agents: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  ),
  subagents: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  ),
  knowledge: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  ),
  channels: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  ),
};

function FeatureIcon({ children }) {
  return (
    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-forge-200 bg-forge-50">
      <svg
        className="h-5 w-5 text-forge-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        {children}
      </svg>
    </div>
  );
}

function HeroBackdrop() {
  return (
    <div
      data-home-hero-background="true"
      className="pointer-events-none absolute inset-0 -z-10 overflow-hidden bg-white"
      aria-hidden="true"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 50% 44%, rgba(240, 249, 255, 0.98) 0%, rgba(248, 250, 252, 0.9) 38%, rgba(255, 255, 255, 0.3) 72%), linear-gradient(128deg, rgba(224, 242, 254, 0.62) 0%, rgba(255, 255, 255, 0) 42%, rgba(226, 232, 240, 0.45) 100%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148, 163, 184, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.2) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          WebkitMaskImage:
            'radial-gradient(ellipse 62% 70% at 50% 46%, transparent 0%, transparent 52%, black 88%)',
          maskImage:
            'radial-gradient(ellipse 62% 70% at 50% 46%, transparent 0%, transparent 52%, black 88%)',
        }}
      />
      <svg
        className="absolute inset-0 hidden h-full w-full text-slate-400/40 md:block"
        viewBox="0 0 1440 720"
        preserveAspectRatio="none"
        fill="none"
      >
        <g stroke="currentColor" strokeWidth="1.25" vectorEffect="non-scaling-stroke">
          <path d="M0 112h176l96-96h112" />
          <path d="M0 208h112l96-96V0" />
          <path d="M1440 112h-176l-96-96h-112" />
          <path d="M1440 208h-112l-96-96V0" />
          <path d="M0 528h176l80 80v112" />
          <path d="M0 624h112l64-64" />
          <path d="M1440 528h-176l-80 80v112" />
          <path d="M1440 624h-112l-64-64" />
        </g>
        <g className="text-forge-300/30" fill="currentColor">
          <circle cx="176" cy="528" r="11" />
          <circle cx="1264" cy="528" r="11" />
        </g>
        <g className="text-forge-500/60" fill="currentColor">
          <circle cx="176" cy="528" r="4" />
          <circle cx="1264" cy="528" r="4" />
        </g>
      </svg>
      <div className="absolute left-1/2 top-[46%] h-72 w-[min(90vw,48rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/75 blur-3xl" />
    </div>
  );
}

function metadataSelector(tagName, attributes) {
  if (tagName === 'meta') {
    return attributes.name
      ? `meta[name="${attributes.name}"]`
      : `meta[property="${attributes.property}"]`;
  }
  return `link[rel="${attributes.rel}"]${attributes.type ? `[type="${attributes.type}"]` : ''}`;
}

function useHomeMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;

    const managedElements = META_TAGS.map(([tagName, attributes]) => {
      const existing = document.head.querySelector(metadataSelector(tagName, attributes));
      const element = existing || document.createElement(tagName);
      const previousAttributes = existing
        ? Array.from(existing.attributes).map(({ name, value }) => [name, value])
        : [];
      const removeOnCleanup = !existing || existing.dataset.homeStatic === 'true';

      element.dataset.homeSeo = 'true';
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      if (!existing) document.head.appendChild(element);

      return { element, previousAttributes, removeOnCleanup };
    });

    document.head.querySelectorAll('script[data-home-static="true"]').forEach((item) => {
      item.remove();
    });
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.homeSeo = 'true';
    script.text = JSON.stringify(STRUCTURED_DATA);
    document.head.appendChild(script);

    return () => {
      document.title = previousTitle === SEO.title ? COMPANY_BRAND_NAME : previousTitle;
      managedElements.forEach(({ element, previousAttributes, removeOnCleanup }) => {
        if (removeOnCleanup) {
          element.remove();
          return;
        }
        Array.from(element.attributes).forEach(({ name }) => element.removeAttribute(name));
        previousAttributes.forEach(([name, value]) => element.setAttribute(name, value));
      });
      script.remove();
    };
  }, []);
}

function Home() {
  const { isAuthenticated } = useAuth();
  useHomeMetadata();
  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="relative isolate overflow-hidden py-20 sm:py-24 lg:py-32">
        <HeroBackdrop />

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="mb-8 inline-flex items-center gap-3 rounded-full border border-slate-200/80 bg-white/75 px-4 py-2 text-sm text-content-secondary shadow-sm backdrop-blur-sm">
            <span className="h-px w-5 bg-gradient-to-r from-accent-purple to-forge-500" />
            {HERO.eyebrow}
          </p>

          <h1 className="mb-6 text-4xl font-bold tracking-tight text-content-primary sm:text-5xl lg:text-6xl">
            {HERO.headline}
          </h1>

          <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-content-secondary sm:text-xl">
            {HERO.body}
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            {isAuthenticated ? (
              <Link to="/agents" className="btn-primary">
                Ir para o painel
              </Link>
            ) : (
              <>
                <a href={PANEL_LEAD_URL} className="btn-primary">
                  {CLOSING.primaryCta}
                </a>
                <a href={CONTACT_URL} className="btn-secondary">
                  {CLOSING.secondaryCta}
                </a>
                <a
                  href={PANEL_LOGIN_URL}
                  className="group inline-flex items-center gap-2 px-3 py-3 text-sm font-medium text-content-secondary transition-colors hover:text-forge-700"
                >
                  Já tenho conta — entrar
                  <span
                    className="transition-transform duration-200 group-hover:translate-x-1"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </a>
              </>
            )}
          </div>

          <a
            href="#o-que-e"
            className="mt-8 inline-flex items-center gap-1 text-sm text-content-muted transition-colors hover:text-forge-600"
          >
            Entenda a plataforma
          </a>
        </div>
      </section>

      <section
        id="o-que-e"
        className="border-t border-surface-border bg-white/50 py-20 lg:py-24"
      >
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
            {INTRODUCTION.eyebrow}
          </p>
          <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-bold text-content-primary sm:text-4xl">
            {INTRODUCTION.headline}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-content-secondary sm:text-lg">
            {INTRODUCTION.body}
          </p>

          <div className="mx-auto mt-12 max-w-3xl rounded-2xl border border-forge-200 bg-forge-50/60 p-8 text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {AUDIENCE.eyebrow}
            </p>
            <h3 className="mt-3 text-2xl font-bold text-content-primary">{AUDIENCE.headline}</h3>
            <p className="mt-4 leading-relaxed text-content-secondary">{AUDIENCE.body}</p>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="border-t border-surface-border py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {STEPS_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {STEPS_SECTION.headline}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-content-secondary">{STEPS_SECTION.body}</p>
          </div>

          <ol className="grid gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <li key={step.number} className="surface-card relative p-6">
                <span className="absolute right-6 top-4 select-none text-4xl font-bold text-forge-100">
                  {step.number}
                </span>
                <h3 className="relative mb-2 text-lg font-semibold text-content-primary">
                  {step.title}
                </h3>
                <p className="relative text-sm leading-relaxed text-content-secondary">
                  {step.description}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section
        id="plataforma"
        className="border-t border-surface-border bg-white/50 py-20 lg:py-24"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {SHOWCASE_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {SHOWCASE_SECTION.headline}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-content-secondary">
              {SHOWCASE_SECTION.body}
            </p>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {SHOWCASES.map((showcase, index) => (
              <article
                key={showcase.id}
                className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12"
              >
                <div className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
                    {showcase.eyebrow}
                  </p>
                  <h3 className="mt-3 text-2xl font-bold leading-tight text-content-primary sm:text-3xl">
                    {showcase.title}
                  </h3>
                  <p className="mt-4 leading-relaxed text-content-secondary">
                    {showcase.description}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {showcase.highlights.map((highlight) => (
                      <li
                        key={highlight}
                        className="flex items-start gap-3 text-sm text-content-secondary"
                      >
                        <span
                          className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-accent-emerald"
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <figure className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                  <img
                    src={`${publicUrl}${showcase.image}`}
                    alt={showcase.imageAlt}
                    width="1200"
                    height="896"
                    loading="lazy"
                    decoding="async"
                    className="h-auto w-full rounded-2xl border border-surface-border bg-white shadow-card-md"
                  />
                </figure>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="clientes-e-parceiros"
        className="border-t border-surface-border py-20 lg:py-24"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-16 max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {TRUST_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {TRUST_SECTION.headline}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-content-secondary">{TRUST_SECTION.body}</p>
          </div>

          <div className="space-y-14">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
                {CLIENTS_GROUP.eyebrow}
              </p>
              <h3 className="mt-3 text-2xl font-bold text-content-primary">
                {CLIENTS_GROUP.headline}
              </h3>
              <ul className="mt-8 grid gap-6 sm:grid-cols-2">
                {CLIENTS.map((item) => (
                  <li key={item.id}>
                    <article className="surface-card flex h-full flex-col p-6">
                      <img
                        src={`${publicUrl}${item.image}`}
                        alt={item.imageAlt}
                        width={item.imageWidth}
                        height={item.imageHeight}
                        loading="lazy"
                        decoding="async"
                        className="mb-5 h-12 w-auto max-w-[10rem] object-contain object-left"
                      />
                      <h4 className="text-lg font-semibold text-content-primary">{item.name}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-content-secondary">
                        {item.description}
                      </p>
                    </article>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
                {PARTNERS_GROUP.eyebrow}
              </p>
              <h3 className="mt-3 text-2xl font-bold text-content-primary">
                {PARTNERS_GROUP.headline}
              </h3>
              <ul className="mt-8 grid gap-6 sm:grid-cols-3">
                {PARTNERS.map((item) => (
                  <li key={item.id}>
                    <article className="surface-card flex h-full flex-col p-6">
                      <img
                        src={`${publicUrl}${item.image}`}
                        alt={item.imageAlt}
                        width={item.imageWidth}
                        height={item.imageHeight}
                        loading="lazy"
                        decoding="async"
                        className="mb-5 h-12 w-auto max-w-[10rem] object-contain object-left"
                      />
                      <h4 className="text-lg font-semibold text-content-primary">{item.name}</h4>
                      <p className="mt-2 text-sm leading-relaxed text-content-secondary">
                        {item.description}
                      </p>
                    </article>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="border-t border-surface-border bg-white/50 py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {FEATURES_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {FEATURES_SECTION.headline}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-content-secondary">
              {FEATURES_SECTION.body}
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((feature) => (
              <article
                key={feature.id}
                className="surface-card p-6 transition-all duration-200 hover:border-forge-200 hover:shadow-card-md"
              >
                <FeatureIcon>{FEATURE_ICONS[feature.id]}</FeatureIcon>
                <h3 className="mb-2 text-lg font-semibold text-content-primary">
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed text-content-secondary">
                  {feature.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="artigos-recentes" className="border-t border-surface-border py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {RECENT_ARTICLES_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {RECENT_ARTICLES_SECTION.headline}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-content-secondary">
              {RECENT_ARTICLES_SECTION.body}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {RECENT_ARTICLES.map((article) => (
              <a
                key={article.href}
                href={article.href}
                className="surface-card flex h-full flex-col p-6 transition-colors hover:border-forge-300"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-forge-600">
                  {article.category}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-content-primary">{article.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-content-secondary">
                  {article.description}
                </p>
                <span className="mt-5 text-sm font-medium text-forge-700">Ler artigo →</span>
              </a>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href={RECENT_ARTICLES_SECTION.allHref}
              className="text-sm font-semibold text-forge-700 transition-colors hover:text-forge-900"
            >
              {RECENT_ARTICLES_SECTION.allLabel}
            </a>
          </div>
        </div>
      </section>

      <section id="duvidas" className="border-t border-surface-border py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forge-600">
              {FAQ_SECTION.eyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-bold text-content-primary sm:text-4xl">
              {FAQ_SECTION.headline}
            </h2>
          </div>

          <div className="divide-y divide-surface-border border-y border-surface-border">
            {FAQS.map((item) => (
              <article key={item.question} className="py-7">
                <h3 className="text-lg font-semibold text-content-primary">{item.question}</h3>
                <p className="mt-3 leading-relaxed text-content-secondary">{item.answer}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-xs text-content-subtle">
            {`Conteúdo atualizado em ${LAST_MODIFIED_LABEL}.`}
          </p>
        </div>
      </section>

      <section className="border-t border-surface-border bg-forge-50/50 py-20 lg:py-24">
        <div className="mx-auto max-w-2xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-2xl font-bold text-content-primary sm:text-3xl">
            {CLOSING.headline}
          </h2>
          <p className="mb-8 text-content-secondary">{CLOSING.body}</p>
          {isAuthenticated ? (
            <Link to="/onboarding" className="btn-primary">
              Criar agente
            </Link>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a href={PANEL_LEAD_URL} className="btn-primary">
                {CLOSING.primaryCta}
              </a>
              <a href={CONTACT_URL} className="btn-secondary">
                {CLOSING.secondaryCta}
              </a>
            </div>
          )}
          <nav
            aria-label="Mais soluções da MAIA"
            className="mt-10 grid gap-4 text-left sm:grid-cols-2"
          >
            {RELATED_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="rounded-2xl border border-surface-border bg-white p-5 transition-colors hover:border-forge-300"
              >
                <span className="block font-semibold text-content-primary">{link.label}</span>
                <span className="mt-2 block text-sm leading-relaxed text-content-secondary">
                  {link.description}
                </span>
              </a>
            ))}
          </nav>
        </div>
      </section>
    </div>
  );
}

export default Home;
